import {useController} from 'react-hook-form';
import {type ChangeEvent, useEffect, useId, useMemo} from 'react';
import {InputTextV2} from '@prtimes-basis/input';
import styles from './foundation-field.module.css';
import {FieldDescription} from '@/features/company-registration/steps/input-step/form-field/field-description';
import {ErrorMessage} from '@/features/company-registration/steps/input-step/form-field/error-message';
import {useCompanyRegistrationFormContext} from '@/features/company-registration/form';
import companyRegistrationPageStyles from '@/features/company-registration/styles.module.css';

export function FoundationField() {
  const {
    control,
    setError,
    setValue,
    trigger,
    watch,
    formState: {isSubmitted},
  } = useCompanyRegistrationFormContext();

  const {
    field: yearField,
    fieldState: {error: yearError},
  } = useController({
    control,
    name: 'companyInfo.foundationYear',
  });
  const {
    field: monthField,
    fieldState: {error: monthError},
  } = useController({
    control,
    name: 'companyInfo.foundationMonth',
  });

  const id = useId();

  const yearErrorMessage = yearError?.message;
  const yearErrorId = `${id}-year-error`;
  const onChangeYear = (event: ChangeEvent<HTMLInputElement>) => {
    setValue('companyInfo.foundationYear', event.target.value);

    if (isSubmitted) {
      void trigger('companyInfo.foundationYear');
      void trigger('companyInfo.foundationMonth');
    }
  };

  const monthErrorMessage = monthError?.message;
  const monthErrorId = `${id}-month-error`;
  const onChangeMonth = (event: ChangeEvent<HTMLInputElement>) => {
    setValue('companyInfo.foundationMonth', event.target.value);

    if (isSubmitted) {
      void trigger('companyInfo.foundationYear');
      void trigger('companyInfo.foundationMonth');
    }
  };

  const dateErrorMessage = useMemo(() => {
    if (yearErrorMessage?.includes('年月') === true) {
      return yearErrorMessage;
    }
  }, [yearErrorMessage]);
  const dateErrorId = `${id}-date-error`;

  useEffect(() => {
    if (dateErrorMessage !== undefined) {
      setError('companyInfo.foundationMonth', {message: dateErrorMessage});
    }
  }, [dateErrorMessage, setError]);

  return (
    <>
      <div className={styles.foundationField}>
        <label
          htmlFor={yearField.name}
          className={companyRegistrationPageStyles.srOnly}
        >
          会社設立日（年）
        </label>
        <div className={styles.container}>
          <div className={styles.inputContainer}>
            <InputTextV2
              ref={yearField.ref}
              id={yearField.name}
              aria-describedby={`${yearErrorId} ${dateErrorId}`}
              aria-invalid={yearErrorMessage !== undefined}
              error={yearErrorMessage !== undefined}
              wrapperClassName={styles.inputWrapper}
              value={watch('companyInfo.foundationYear')}
              onChange={onChangeYear}
            />
            <span>年</span>
          </div>
          {yearErrorMessage !== undefined && dateErrorMessage === undefined && (
            <ErrorMessage id={yearErrorId}>{yearErrorMessage}</ErrorMessage>
          )}
        </div>
        <label
          htmlFor={monthField.name}
          className={companyRegistrationPageStyles.srOnly}
        >
          会社設立日（月）
        </label>
        <div className={styles.container}>
          <div className={styles.inputContainer}>
            <InputTextV2
              ref={monthField.ref}
              id={monthField.name}
              aria-describedby={`${monthErrorId} ${dateErrorId}`}
              aria-invalid={
                monthErrorMessage !== undefined ||
                dateErrorMessage !== undefined
              }
              error={
                monthErrorMessage !== undefined ||
                dateErrorMessage !== undefined
              }
              value={watch('companyInfo.foundationMonth')}
              wrapperClassName={styles.inputWrapper}
              onChange={onChangeMonth}
            />
            <span>月</span>
          </div>
          {monthErrorMessage !== undefined &&
            dateErrorMessage === undefined && (
              <ErrorMessage id={monthErrorId}>{monthErrorMessage}</ErrorMessage>
            )}
        </div>
      </div>

      {dateErrorMessage !== undefined && (
        <ErrorMessage id={dateErrorId}>{dateErrorMessage}</ErrorMessage>
      )}

      <FieldDescription>
        会社設立日をご登録いただくと、設立日から2年未満の企業は「
        <a
          href='/startup_free/'
          target='_blank'
          className={companyRegistrationPageStyles.link}
        >
          スタートアップチャレンジ
        </a>
        」にエントリー できます。
      </FieldDescription>
    </>
  );
}
